import { useDeferredValue, useRef } from 'react';
import { Box, BoxProps, getVariant } from '@apia/theme';
import { CurrentTabContext, useTabsContext } from './util';
import { observer } from 'mobx-react-lite';
import { Tab } from './tabsController';
import { Accordion, AccordionItem, LoaderSpinner } from '../components';
import { customEvents } from '@apia/util';

const InnerRenderer = observer(({ tab }: { tab: Tab }) => {
  const state = tab.state;
  const Render = useDeferredValue(state.content);
  let accordion = null;
  const tabContent = (
    <Box className="tabs__Item" sx={{ height: '100%' }}>
      <Render tab={tab} />
      {state.isLoading && (
        <Box className="tabs__content__spinner__wrapper">
          <LoaderSpinner />
        </Box>
      )}
    </Box>
  );
  if (tab.state.asAccordion)
    accordion = (
      <AccordionItem
        id={tab.id}
        key={tab.id}
        defaultExpanded={true}
        buttonProps={{
          ariaLabel: tab.state.label,
          label: tab.state.label,
        }}
      >
        {tabContent}
      </AccordionItem>
    );

  const uns = useRef(() => {});

  return (
    <Box
      aria-hidden={!state.isOpen}
      aria-labelledby={`tab-${tab.controller.id}-${state.id}`}
      className={`tabs__content ${state.isOpen ? 'open' : ''}`}
      data-id={state.id}
      id={`tabpanel-${tab.controller.id}-${state.id}`}
      data-test-id={`tab-content-${tab.controller.id}-${state.id}`}
      key={state.id}
      role="tabpanel"
      sx={{
        display: state.isOpen ? undefined : 'none',
      }}
      ref={(el) => {
        uns.current();
        if (el instanceof HTMLElement) {
          const handleFocus = () => {
            if (tab.controller.state.listenFocusEvents !== false) {
              tab.open();
            }
          };
          el.addEventListener(customEvents.focus, handleFocus);
          uns.current = () => {
            el.removeEventListener(customEvents.focus, handleFocus);
          };
        }
      }}
    >
      {accordion ?? tabContent}
    </Box>
  );
});

export const TabsContent = observer((props: Omit<BoxProps, 'children'>) => {
  const controller = useTabsContext();

  if (!controller) return null;

  return (
    <Accordion
      className="tabs__content__wrapper"
      {...getVariant('layout.common.tabs.primary.content')}
      {...props}
    >
      {controller.tabsList.map((tab) => {
        return (
          <CurrentTabContext.Provider value={tab} key={tab.id}>
            <InnerRenderer tab={tab} />
          </CurrentTabContext.Provider>
        );
      })}
    </Accordion>
  );
});
